import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider
} from "@mui/material";
import { motion } from "framer-motion";
import HandshakeIcon from "@mui/icons-material/Handshake";
import SupportChatBot from "../components/SupportChatBot";

/* ------------------ DATA ------------------ */

const pricingData = {
  INR: {
    symbol: "₹",
    plans: [
      {
        name: "Elite",
        price: "49,999",
        desc: "Ideal for individuals & early-stage startups",
        category: "Website Development",
        code: "ZNX-WEB-ELITE",
        features: [
          "Professional Website",
          "Basic Web App",
          "Core Software Setup",
          "Email & Chat Support",
          "Basic Maintenance",
          "Brand Presence Setup"
        ],
        link: "https://rzp.io/rzp/zolang-elite"
      },
      {
        name: "Premium",
        price: "99,999",
        desc: "Best for growing & scaling businesses",
        highlight: true,
        category: "Digital Solutions Suite",
        code: "ZNX-SUITE-DIGI",
        features: [
          "Website + Advanced Web App",
          "Custom Software Modules",
          "Priority Support",
          "Ongoing Maintenance",
          "Marketing & Branding Strategy",
          "Performance Optimization"
        ],
        link: "https://rzp.io/rzp/zolang-premium"
      },
      {
        name: "Enterprise",
        price: "1,99,999",
        desc: "For enterprises & long-term engagements",
        category: "Enterprise Solutions",
        code: "ZNX-SUITE-ENT",
        features: [
          "Full Website + Web App Suite",
          "Custom Software Solutions",
          "Dedicated Support",
          "Continuous Maintenance",
          "Brand & Growth Consulting",
          "Technology Roadmapping"
        ],
        link: "https://rzp.io/rzp/zolang-enterprise"
      }
    ]
  }
};

/* ------------------ HELPERS ------------------ */

const calculateAdvance = (price) => {
  const numericPrice = Number(price.replace(/,/g, ""));
  return Math.round(numericPrice * 0.3).toLocaleString();
};

/* ------------------ COMPONENT ------------------ */

const CollaborateAndPricing = () => {
  const [currency, setCurrency] = useState("INR");
  const { symbol, plans } = pricingData[currency];

  return (
    <Box sx={{ py: { xs: 8, md: 12 } }}>
<SupportChatBot/>
      {/* HERO */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Typography
          variant="h2"
          sx={{ fontWeight: 900, mb: 2 }}
        >
          Transparent Pricing. Serious Results.
        </Typography>

        <Typography
          sx={{
            color: "rgba(255,255,255,0.75)",
            maxWidth: 760,
            mx: "auto"
          }}
        >
          Start with a 30% advance. Choose a plan or book a consultation.
          Service codes ensure clarity, accountability, and smooth delivery.
        </Typography>
      </Box>
      {/* COMMON BOOKING – PRIMARY CTA */}
      <Box sx={{ maxWidth: 900, mx: "auto", mb: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          viewport={{ once: true }}
        >
          <Card
            sx={{
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(30,35,80,0.95), rgba(45,50,100,0.95))",
              border: "2px dashed rgba(255,77,77,0.6)",
              textAlign: "center"
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Chip
                label="COMMON BOOKING"
                sx={{
                  mb: 2,
                  background: "#ff4d4d",
                  color: "#fff",
                  fontWeight: 700
                }}
              />

              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                Not Sure Which Plan?
              </Typography>

              <Typography sx={{ color: "rgba(255,255,255,0.75)", mb: 3 }}>
                Reserve your slot now. Final scope, pricing, and plan
                will be confirmed after consultation.
              </Typography>

              <Stack
                direction="row"
                justifyContent="center"
                flexWrap="wrap"
                gap={1.2}
                mb={3}
              >
                {[
                  "ZNX-COM-11",
                  "ZNX-WEB-01",
                  "ZNX-APP-02",
                  "ZNX-SW-03",
                  "ZNX-MNT-04",
                  "ZNX-SUITE-05",
                  "ZNX-ENT-09"
                ].map((code) => (
                  <Chip key={code} label={code} sx={{ fontWeight: 700 }} />
                ))}
              </Stack>

              <Typography sx={{ color: "#ff8080", fontWeight: 700, mb: 3 }}>
                Pay 30% Advance to Secure Your Slot
              </Typography>

              <Button
                variant="contained"
                size="large"
                href="https://rzp.io/rzp/zolang-common"
                target="_blank"
                sx={{ px: 6, py: 1.5, fontWeight: 700 }}
              >
                Proceed with Booking
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </Box>

      {/* PRICING PLANS */}
      <Grid container spacing={4} sx={{ maxWidth: 1200, mx: "auto" }}>
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={plan.name}>
            <motion.div
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.12 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  height: "100%",
                  borderRadius: 4,
                  background:
                    "linear-gradient(135deg, rgba(10,17,40,0.95), rgba(26,31,61,0.95))",
                  border: plan.highlight
                    ? "2px solid #ff4d4d"
                    : "1px solid rgba(255,255,255,0.15)",
                  position: "relative"
                }}
              >
                {plan.highlight && (
                  <Chip
                    label="MOST CHOSEN"
                    sx={{
                      position: "absolute",
                      top: -12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#ff4d4d",
                      color: "#fff",
                      fontWeight: 700
                    }}
                  />
                )}

                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {plan.name}
                  </Typography>

                  <Typography sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}>
                    {plan.desc}
                  </Typography>

                  <Stack direction="row" spacing={1} mb={2}>
                    <Chip label={plan.category} size="small" variant="outlined" />
                    <Chip label={plan.code} size="small" sx={{ fontWeight: 700 }} />
                  </Stack>

                  <Divider sx={{ mb: 2 }} />

                  <Typography variant="h4" sx={{ fontWeight: 900 }}>
                    {symbol}{plan.price}
                  </Typography>

                  <Typography sx={{ color: "#ff8080", fontWeight: 700, mb: 3 }}>
                    Pay 30% Advance: {symbol}{calculateAdvance(plan.price)}
                  </Typography>

                  <Stack spacing={1} mb={3}>
                    {plan.features.map((f, i) => (
                      <Typography key={i}>• {f}</Typography>
                    ))}
                  </Stack>

                  <Button
                    fullWidth
                    variant={plan.highlight ? "contained" : "outlined"}
                    href={plan.link}
                    target="_blank"
                    sx={{ py: 1.3, fontWeight: 700 }}
                  >
                    Pay 30% Advance
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* FINAL CTA */}
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Button
          variant="contained"
          startIcon={<HandshakeIcon />}
          href="/contact"
          sx={{ px: 5, py: 1.5, fontWeight: 700 }}
        >
          Request Custom Quotation
        </Button>
      </Box>
    </Box>
  );
};

export default CollaborateAndPricing;
